"use client";

import type { PostData } from "@tosize/panel-configurator/src/context/PostDataContext";
import type { Workpiece as PanelWorkpiece } from "@tosize/panel-configurator/src/lib/workpiece";
import { Form, SubmitButton, useDialogContext, useNotifications, useZodForm } from "@tosize/ui";
import { useRouter } from "next/navigation";
import { Fragment, type PropsWithChildren, type ReactNode } from "react";
import { z } from "zod";
import { createCustomerProjectFromCart, onAddToProject } from "~/app/[lang]/cart/actions";
import type { LangParams } from "~/app/[lang]/layout";

export interface CustomerProjectFormProps extends LangParams, PropsWithChildren {
	projects: { id: string; name: string }[];
	label: string;
	nameForm: ReactNode;
	projectForm: ReactNode;
	innerMessages: {
		addedToProject: string;
		addToProjectError: string;
		viewProject: string;
	};
}

type Variant =
	| {
			variant: "cart";
			data?: never;
			workpiece?: never;
	  }
	| { variant: "item"; data: PostData; workpiece: PanelWorkpiece };

export function CustomerProjectForm(props: CustomerProjectFormProps & Variant) {
	const {
		lang,
		children,
		projects,
		label,
		nameForm,
		projectForm,
		innerMessages,
		variant,
		data,
		workpiece,
	} = props;

	const form = useZodForm({
		schema: z.object({
			name: z.string().optional(),
			projectId: z.string().optional(),
			newProject: z.literal("1").or(z.literal("0")),
		}),
		defaultValues: {
			newProject: "1",
		},
	});

	const { watch } = form;
	const newProject = watch("newProject");
	const router = useRouter();
	const dialog = useDialogContext();
	const { notify } = useNotifications();

	return (
		<Fragment>
			<Form
				className="flex flex-col gap-4"
				form={form}
				onSubmit={async (submittedData) => {
					dialog?.setForceOpen(true);

					try {
						const route =
							!submittedData.projectId || submittedData.projectId === "newProject"
								? "/customer/projects"
								: `/customer/projects/${submittedData.projectId}`;

						if (variant === "item") {
							await onAddToProject(
								lang,
								data,
								workpiece,
								submittedData.name ?? "",
								submittedData.projectId === "newProject" ? undefined : submittedData.projectId,
							);
						} else {
							if (submittedData.projectId) {
								await createCustomerProjectFromCart({ lang, projectId: submittedData.projectId });
							} else {
								await createCustomerProjectFromCart({ lang, name: submittedData.name });
							}
						}

						const name =
							!submittedData.projectId || submittedData.projectId === "newProject"
								? submittedData.name
								: projects.find((p) => p.id === submittedData.projectId)?.name;

						notify({
							variant: "success",
							title: `${innerMessages.addedToProject}: ${name}`,
							subtitle: innerMessages.viewProject,
							action: {
								altText: innerMessages.viewProject,
								onClick: () => void router.push(route),
							},
						});

						if (dialog?.onOpenChange) {
							dialog?.onOpenChange(false);
						} else {
							router.push(route);
						}
					} catch (error) {
						notify({
							variant: "danger",
							title: innerMessages.addToProjectError,
							subtitle: error instanceof Error ? error.message : undefined,
						});
					}

					dialog?.setForceOpen(false);
				}}
			>
				{children}

				{newProject === "1" ? nameForm : projectForm}

				<SubmitButton className="sm:self-start">{label}</SubmitButton>
			</Form>
		</Fragment>
	);
}
